// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { useQuery } from '@tanstack/react-query';
import { userManagementApi, UserRole as AuthRole } from '@tls/auth';
import { Role } from 'model/Role';
import { useCallback } from 'react';

// matches SW91.Host's Permissions.AppKey (src/SW91.Host/Auth/Permissions.cs)
const AppKey = 'easymodel';

// A role that has not been persisted yet has no id, everything else is checked as usual.
type UnsavedAuthRole = Omit<AuthRole, 'id'> & { id?: string };

function toAuthRole(role: Role): AuthRole {
  const mapped: UnsavedAuthRole = {
    name: role.name,
    isBuiltIn: role.isBuiltIn ?? false,
    permissions: role.permissions ?? [],
  };
  // Only send the id once it exists — putRole upserts and types id as required.
  if (role.id) mapped.id = role.id;
  return mapped as AuthRole;
}

export default function useRolesApi() {
  const {
    data: roles,
    isLoading: loadingRoles,
    refetch: getRoles,
  } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: () => userManagementApi.getRoles(),
  });

  const { data: permissions, isLoading: loadingPermissions } = useQuery<string[]>({
    queryKey: ['permissions'],
    queryFn: () => userManagementApi.getAvailablePermissions(AppKey),
  });

  // putRole upserts, so adding and editing are the same call
  const saveRole = useCallback(
    async (role: Role) => {
      const result = await userManagementApi.putRole(toAuthRole(role));
      getRoles();
      return result;
    },
    [getRoles]
  );

  const deleteRole = useCallback(
    async (role: Role) => {
      if (!role.id) return;
      const result = await userManagementApi.deleteRole(role.id);
      getRoles();
      return result;
    },
    [getRoles]
  );

  return {
    roles: Array.isArray(roles) ? roles : [],
    loadingRoles,
    getRoles,
    editRole: saveRole,
    addRole: saveRole,
    deleteRole,
    permissions,
    loadingPermissions,
  };
}
